import React, { SyntheticEvent, useMemo } from 'react';
import classnames from 'classnames';
import _debounce from 'lodash/debounce';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { UseGlobalFiltersInstanceProps } from 'react-table';
import { defaultInputStyleFunction } from '@app/form';
import { useTranslation } from 'react-i18next';

const useInputStyle = createUseStyles(defaultInputStyleFunction);
const useStyles = createUseStyles((theme: Theme) => ({
  input: {
    maxWidth: theme.maxContentWidth.smallBlock,
    minHeight: theme.height.small,
    paddingLeft: theme.marginBase * 5,
  },
  label: {
    position: 'relative',
  },
  iconContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: theme.marginBase * 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.inkTertiary,
  },
}));

interface Props<D extends object> {
  tableInfos: UseGlobalFiltersInstanceProps<D> & { state: any };
  className?: string;
}

export const DataTableSearch = function <D extends object>({
  tableInfos: { setGlobalFilter },
  className,
}: Props<D>) {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const defaultInputClasses = useInputStyle({ theme });
  const { t } = useTranslation();

  const onChange = useMemo(() => {
    const _setGlobalFilter = _debounce(setGlobalFilter, 30);
    return (e: SyntheticEvent<HTMLInputElement>) => {
      _setGlobalFilter(e.currentTarget.value || undefined);
    };
  }, [setGlobalFilter]);

  return (
    <label className={classnames(classes.label, className)}>
      <div className={classes.iconContainer}>
        <i className={classnames(classes.icon, 'ri-search-line')} />
      </div>
      <input
        type="text"
        className={classnames(defaultInputClasses.input, classes.input)}
        onChange={onChange}
        placeholder={t('datatable.search')}
      />
    </label>
  );
};
